import { useEffect, useState } from "react";
import { api } from "./api";
import { Link } from "./Link";
import { dueLabel, formatWhen, type ReviewSummary } from "./review-api";
import { Field, Notice } from "./ui";

interface Queue {
  reviews: ReviewSummary[];
  coaches: Array<{ id: string; name: string | null; email: string }> | null;
}

const who = (review: ReviewSummary) => review.playerName ?? review.playerEmail ?? "Player";

export function Coaching({ go }: { go: (href: string) => void }) {
  const [queue, setQueue] = useState<Queue | null>(null);
  const [error, setError] = useState("");
  const [reload, setReload] = useState(0);
  useEffect(() => {
    let current = true;
    setError("");
    api<Queue>("/coach/reviews")
      .then((result) => current && setQueue(result))
      .catch((e: Error) => current && setError(e.message));
    return () => {
      current = false;
    };
  }, [reload]);

  const ready = queue?.reviews.filter((r) => r.status === "in_review") ?? [];
  const waiting = queue?.reviews.filter((r) => r.status === "awaiting_upload") ?? [];
  const done = queue?.reviews.filter((r) => r.status === "done") ?? [];

  return (
    <main id="main" className="app-main">
      <div className="tournament-heading">
        <h1>Video reviews</h1>
        <p>Pick any review that's ready. Each one is due 48 hours after the footage arrives.</p>
      </div>
      {error ? (
        <Notice error>
          {error}{" "}
          <button className="text-button" onClick={() => setReload((n) => n + 1)}>
            Retry
          </button>
        </Notice>
      ) : !queue ? (
        <Notice>Loading reviews…</Notice>
      ) : (
        <div className="review-queue">
          <section className="stack">
            <h2>Ready to review</h2>
            {ready.length === 0 ? (
              <p>No footage is waiting for a coach. New uploads appear here, and coaches get an email for each one.</p>
            ) : (
              <ul className="plain-list played-list">
                {ready.map((review) => (
                  <li key={review.id}>
                    <div>
                      <Link go={go} href={`/coach/reviews/${review.id}`}>
                        {who(review)}
                      </Link>
                      <small>
                        {[
                          review.coach ? `Picked by ${review.coach.name ?? review.coach.email}` : "Not picked yet",
                          review.video.state === "processing" ? "Video still converting" : null,
                          review.free ? "Free review" : null,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </small>
                    </div>
                    {review.dueAt && (
                      <span className={review.dueAt < Date.now() ? "played-result overdue" : "played-result"}>
                        {dueLabel(review.dueAt)}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
          {waiting.length > 0 && (
            <section className="stack">
              <h2>Waiting for footage</h2>
              <ul className="plain-list played-list">
                {waiting.map((review) => (
                  <li key={review.id}>
                    <div>
                      <Link go={go} href={`/coach/reviews/${review.id}`}>
                        {who(review)}
                      </Link>
                      <small>
                        {review.free ? "Free review" : "Paid review"} opened {formatWhen(review.createdAt)}
                      </small>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          )}
          {done.length > 0 && (
            <section className="stack">
              <h2>Done in the last 2 weeks</h2>
              <ul className="plain-list played-list">
                {done.map((review) => (
                  <li key={review.id}>
                    <div>
                      <Link go={go} href={`/coach/reviews/${review.id}`}>
                        {who(review)}
                      </Link>
                      <small>{review.coach?.name ?? review.coach?.email ?? ""}</small>
                    </div>
                    {review.doneAt && <span className="played-result">{formatWhen(review.doneAt)}</span>}
                  </li>
                ))}
              </ul>
            </section>
          )}
          <FreeReview opened={() => setReload((n) => n + 1)} />
        </div>
      )}
    </main>
  );
}

function FreeReview({ opened }: { opened: () => void }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ text: string; error: boolean } | null>(null);
  return (
    <form
      className="stack invite-form"
      onSubmit={async (event) => {
        event.preventDefault();
        setBusy(true);
        setMessage(null);
        try {
          await api("/coach/reviews", { email: email.trim(), ...(name.trim() ? { name: name.trim() } : {}) });
          setMessage({ text: `We emailed an upload link to ${email.trim()}.`, error: false });
          setEmail("");
          setName("");
          opened();
        } catch (e) {
          setMessage({ text: (e as Error).message, error: true });
        } finally {
          setBusy(false);
        }
      }}
    >
      <h2>Offer a free review</h2>
      <p>The player gets the same upload link as a paying player, with no charge.</p>
      <Field label="Player's email">
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoComplete="off" />
      </Field>
      <Field label="Player's name (optional)">
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={80} autoComplete="off" />
      </Field>
      {message && <Notice error={message.error}>{message.text}</Notice>}
      <div className="actions">
        <button className="primary" disabled={busy}>
          {busy ? "Sending…" : "Send upload link"}
        </button>
      </div>
    </form>
  );
}
